import React, { useState, useContext } from 'react';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useToast } from './hooks/ToastContext';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox1,
  ModernCard
} from './Templates';
import Spinner from './common/Spinner';
import './SimpleAccountDelete.css';

/**
 * SimpleAccountDelete Component
 * For direct integration into Settings page
 * Immediately soft deletes account when confirmed
 */
function SimpleAccountDelete() {
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');

  /**
   * Handle account deletion
   */
  const handleDeleteAccount = async () => {
    if (!acknowledged) {
      addToast({
        type: 'error',
        title: 'Confirmation Required',
        description: 'Please confirm you understand the account deletion process.'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Call the simple deletion function
      const { data, error } = await supabase.rpc('delete_my_account', {
        deletion_reason_param: deletionReason || 'User deleted account from settings'
      });

      if (error) throw error;

      if (data.success) {
        addToast({
          type: 'success',
          title: 'Account Deleted',
          description: 'Your account has been hidden. You can restore it anytime by logging in again.'
        });

        // Sign out the user
        await supabase.auth.signOut();
        
        // Redirect to home page
        navigate('/');
      } else {
        throw new Error(data.message || 'Failed to delete account');
      }

    } catch (error) {
      console.error('Error deleting account:', error);
      addToast({
        type: 'error',
        title: 'Deletion Failed',
        description: error.message || 'Failed to delete account. Please ensure you have no active rides or packages.'
      });
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  if (isLoading) {
    return (
      <div className="simple-delete-loading">
        <Spinner size="small" />
        <span>Deleting account...</span>
      </div>
    );
  }

  return (
    <div className="simple-account-delete">
      {!showConfirmation ? (
        <div className="delete-button-container">
          <Button
            variant="destructive"
            onClick={() => setShowConfirmation(true)}
          >
            Delete Account
          </Button>
          <p className="delete-info">
            Hide your account while preserving all data. You can restore it anytime.
          </p>
        </div>
      ) : (
        <ModernCard className="confirmation-card">
          <h3 className="confirmation-title">🔄 Delete Account</h3>
          
          <div className="info-section">
            <p><strong>What happens when you delete your account:</strong></p>
            <ul>
              <li>✅ Your account will be hidden from other users</li>
              <li>✅ All your data stays safe (reviews, history, etc.)</li>
              <li>✅ You can restore everything by logging in again</li>
              <li>❌ You cannot create new posts or packages while deleted</li>
            </ul>
          </div>

          <div className="reason-section">
            <label htmlFor="deletion-reason">
              Why are you leaving? (Optional)
            </label>
            <textarea
              id="deletion-reason"
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              placeholder="Help us improve BrauksimKopa..."
              rows={2}
              maxLength={200}
            />
          </div>

          <div className="acknowledgment-section">
            <Checkbox1
              id="delete-acknowledgment"
              label="I understand this action will hide my account and I can restore it later"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
          </div>

          <div className="confirmation-actions">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={!acknowledged || isLoading}
            >
              Delete Account
            </Button>
          </div>
        </ModernCard>
      )}
    </div>
  );
}

export default SimpleAccountDelete;